<template>
    <div class="kv-filter-container">
        <div class="kv-filter-label">{{ label }}</div>

        <KvPopup max-width="400px" min-width="320px" ref="popup">
            <template #trigger="{ toggle }">
                <span
                    class="kv-filter-input"
                    :title="valueLabel"
                    @click="toggle"
                >
                    {{ valueLabel }}
                    <Icon icon-class="ui-chevron-down" />
                    <span
                        class="kv-remove-filter"
                        v-if="removable"
                        @click.stop="$emit('remove')"
                    >
                        <Icon icon-class="ui-x-close" size="2" color="#212529" />
                    </span>
                </span>
            </template>

            <template #content="{ toggle }">
                <div class="kv-client-filter-content">
                    <kv-search-select
                        :placeholder="t('reportFilters-search_client_placeholder')"
                        api-endpoint="place/clients"
                        fetch-item-endpoint="place/client"
                        :display-field="getClientDisplay"
                        secondary-field="whatsapp"
                        value-field="clients_id"
                        :min-search-length="3"
                        :additional-params="{ source: 'clients-filter' }"
                        :show-clear-button="true"
                        v-model="value"
                        @select="(client) => selectClient(client, toggle)"
                        @clear="reset"
                    />
                </div>
            </template>
        </KvPopup>
    </div>
</template>

<style lang="less" scoped>
.kv-client-filter-content {
    min-width: 280px;

    ::v-deep .kv-search-input-wrapper {
        margin-bottom: 0;
    }

    ::v-deep .kv-search-input {
        padding: 8px 36px 8px 12px;
        font-size: 13px;
        height: 36px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        background: #fff;
    }

    ::v-deep .kv-search-results {
        position: relative;
        margin-top: 8px;
        max-height: 240px;
        overflow-y: auto;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        box-shadow: none;
    }
}
</style>

<script>
import KvSearchSelect from "../../ui/kv-search-select.vue";
import KvPopup from "../../ui/kv-popup.vue";
import Icon from "../../icon/icon";
import keviApi from "../../../keviApi";

export default {
    name: "filter-client",
    components: {
        KvSearchSelect,
        KvPopup,
        Icon,
    },
    props: {
        name: {
            type: String,
            default: "filter-client",
        },
        label: {
            type: String,
            default: "",
        },
        currentValue: {
            type: [String, Number],
            default: null,
        },
        allowReset: {
            type: Boolean,
            default: false,
        },
        removable: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            value: this.currentValue !== undefined ? this.currentValue : null,
            selectedLabel: "",
        };
    },
    computed: {
        valueLabel() {
            if (this.selectedLabel) {
                const label = this.selectedLabel;
                return label.length > 30 ? label.substr(0, 27) + "..." : label;
            }
            return this.t("reportFilters-select");
        },
    },
    created() {
        this.onFilterClientEvent = (client) => {
            this.selectClient({ ...client, clients_id: client.id });
        };
    },
    mounted() {
        this.$root.$on("filter-client", this.onFilterClientEvent);
        if (this.value) {
            this.loadClientLabel(this.value);
        }
    },
    beforeDestroy() {
        this.$root.$off("filter-client", this.onFilterClientEvent);
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        getClientDisplay(client) {
            if (client.first_name) {
                return `${client.first_name} ${client.last_name || ""}`.trim();
            }
            if (client.whatsapp) {
                return client.whatsapp;
            }
            return this.t("reportFilters-client_no_registration");
        },
        loadClientLabel(id) {
            if (!id) {
                this.selectedLabel = "";
                return;
            }
            const options = keviApi.getOptions("place/client/" + id, "GET");
            this.$http(options)
                .then((response) => {
                    const client = response.body || {};
                    this.selectedLabel = this.getClientDisplay(client);
                })
                .catch(() => {
                    this.selectedLabel = "";
                });
        },
        selectClient(client, toggle) {
            const previousId = this.value;
            if (client) {
                const id = client.clients_id || client.id;
                if (id) {
                    this.value = id;
                    this.selectedLabel = this.getClientDisplay(client);
                    // Close only on a real user change. kv-search-select re-emits
                    // the current value when it mounts, which would otherwise
                    // open-and-immediately-close the popup.
                    if (
                        typeof toggle === "function" &&
                        String(id) !== String(previousId)
                    ) {
                        toggle(false);
                    }
                }
            } else {
                this.value = null;
                this.selectedLabel = "";
            }
        },
        reset() {
            this.value = null;
            this.selectedLabel = "";
        },
        changed() {
            const val = this.value === undefined ? null : this.value;
            this.$emit("changed", val);
        },
    },
    watch: {
        value(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.changed();
            }
        },
        currentValue: {
            immediate: true,
            handler(newValue) {
                if (newValue !== undefined && newValue !== this.value) {
                    this.value = newValue;
                    if (newValue) {
                        this.loadClientLabel(newValue);
                    } else {
                        this.selectedLabel = "";
                    }
                }
            },
        },
    },
};
</script>
